import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMessage } from '../../contexts/MessageContext'
import { useApi } from '../../utils/api'
import { FormLabel, FormInput, FormButton, CancelButton } from '../../components/forms/FormElements'
import { PageTitleBar } from '../../components/PageTitleBar'

interface FormData {
  title: string;
}

interface ApiResponse {
  data: {
    id: number;
    title: string;
    created_at: string;
    updated_at: string;
  };
  success: boolean;
}

export default function EditContentGroup() {
  const { id } = useParams<{ id: string }>()
  const { setMessage } = useMessage()
  const { fetchWithAuth } = useApi()
  const [formData, setFormData] = useState<FormData>({
    title: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  const fetchContentGroup = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await fetchWithAuth(`/admin/content-groups/${id}`)
      if (!response.ok) {
        throw new Error('Failed to fetch content group')
      }
      const apiResponse: ApiResponse = await response.json()
      
      if (apiResponse.success && apiResponse.data) {
        setFormData({
          title: apiResponse.data.title || '',
        })
      } else {
        throw new Error('Invalid data structure received from API')
      }
    } catch (err) {
      console.error('Error fetching content group:', err)
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setIsLoading(false)
    }
  }, [id, fetchWithAuth])

  useEffect(() => {
    fetchContentGroup()
  }, [fetchContentGroup])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prevData => ({ ...prevData, [name]: value }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetchWithAuth(`/admin/content-groups/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.title,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to update content group')
      }

      const data = await response.json()
      console.log('Content group updated:', data)
      setMessage('Content group successfully updated!')
      navigate('/content-groups')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <main className="container mx-auto px-4 py-8">
        <PageTitleBar
          title="Edit Content Group"
          breadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Content Groups', href: '/content-groups' },
            { label: 'Edit Content Group' },
          ]}
        />

        <div className="bg-white rounded-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <FormLabel htmlFor="title">TITLE</FormLabel>
              <FormInput
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="flex justify-end space-x-3">
              <CancelButton type="button" onClick={() => navigate('/content-groups')}>
                Cancel
              </CancelButton>
              <FormButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Updating...' : 'Update'}
              </FormButton>
            </div>
            {error && <div className="text-red-500 mt-2">{error}</div>}
          </form>
        </div>
      </main>
    </div>
  )
} 